/**
 * Original + COs = Revised contract display (billed % uses revised total).
 */

import { formatCurrency } from '../../lib/formatters'

export interface ContractBreakdownProps {
  originalContract: string | number | null | undefined
  changeOrderTotal?: string | number | null | undefined
  /** When set, revised contract is Σ scheduled values (always). */
  scheduledSum?: string | number | null | undefined
  /** inline one-liner vs stacked */
  variant?: 'inline' | 'stacked'
  className?: string
  'data-testid'?: string
}

function parseNum(raw: string | number | null | undefined): number {
  if (typeof raw === 'number') return raw
  return parseFloat(String(raw ?? '0')) || 0
}

export function revisedContractTotal(
  original: string | number | null | undefined,
  cos: string | number | null | undefined,
  scheduledSum?: string | number | null | undefined,
): number {
  if (scheduledSum != null && scheduledSum !== '') {
    const sum = parseNum(scheduledSum)
    if (Number.isFinite(sum)) return sum
  }
  return parseNum(original) + parseNum(cos)
}

export default function ContractBreakdown({
  originalContract,
  changeOrderTotal,
  scheduledSum,
  variant = 'inline',
  className,
  'data-testid': testId,
}: ContractBreakdownProps) {
  const orig = parseNum(originalContract)
  const revised = revisedContractTotal(originalContract, changeOrderTotal, scheduledSum)
  const cos = Number.isFinite(revised) ? revised - orig : parseNum(changeOrderTotal)

  if (cos <= 0) {
    return (
      <span className={className} data-testid={testId}>
        {formatCurrency(orig)}
      </span>
    )
  }

  if (variant === 'stacked') {
    return (
      <div className={className} data-testid={testId} style={{ lineHeight: 1.45 }}>
        <div>
          <span style={{ color: 'var(--ink4)', fontSize: 11 }}>Original </span>
          <span className="mono-c">{formatCurrency(orig)}</span>
          <span style={{ color: 'var(--ink4)', fontSize: 11 }}> + COs </span>
          <span className="mono-c">{formatCurrency(cos)}</span>
        </div>
        <div style={{ fontWeight: 600, color: 'var(--ink)' }}>
          Revised {formatCurrency(revised)}
        </div>
      </div>
    )
  }

  return (
    <span className={className} data-testid={testId} title={`Original ${formatCurrency(orig)} + COs ${formatCurrency(cos)}`}>
      Original {formatCurrency(orig)} + COs {formatCurrency(cos)} = Revised {formatCurrency(revised)}
    </span>
  )
}
